import React, { useEffect, useState } from 'react';

const isMobileDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isSmallViewport = window.innerWidth <= 768;
  return isMobileUA || isSmallViewport;
};

const isAppInstalled = () => {
  if (typeof window === 'undefined') return false;

  // Detectar si la app está realmente instalada (no usar localStorage)
  const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const isIOSStandalone = window.navigator.standalone === true;
  
  // Si la app NO está instalada pero el localStorage dice que sí, limpiarlo
  const localFlag = localStorage.getItem('ayudalegal_pwa_installed') === 'true';
  if (localFlag && !isStandalone && !isIOSStandalone) {
    console.log('🧹 Limpiando flag de instalación obsoleto - app no detectada como instalada');
    localStorage.removeItem('ayudalegal_pwa_installed');
    return false;
  }

  return isStandalone || isIOSStandalone;
};

const isHomeView = () => {
  if (typeof window === 'undefined') return true;
  const params = new URLSearchParams(window.location.search || '');
  const view = params.get('view');
  return !view || view === 'home';
};

function PwaInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Debug: mostrar estado actual
    console.log('📱 PWA Banner - Verificando condiciones...');
    console.log('   - Es móvil:', isMobileDevice());
    console.log('   - Es home view:', isHomeView());
    console.log('   - App instalada:', isAppInstalled());
    console.log('   - localStorage flag:', localStorage.getItem('ayudalegal_pwa_installed'));
    
    if (!isMobileDevice() || !isHomeView() || isAppInstalled()) {
      console.log('⚠️  Banner no se mostrará (no cumple condiciones)');
      return;
    }

    const ua = (navigator.userAgent || '').toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(ua);
    setIsIOS(isIOSDevice);

    const handleBeforeInstallPrompt = (e) => {
      console.log('🎉 Evento beforeinstallprompt disparado!');
      e.preventDefault();
      // Guardamos el evento para usarlo cuando el usuario pulse "Instalar"
      setDeferredPrompt(e);
      console.log('✅ deferredPrompt guardado');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Mostrar siempre el banner en móviles (Android + iOS)
    setIsVisible(true);
    console.log('📱 Banner PWA mostrado en dispositivo móvil');

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  if (!isVisible) return null;

  const handleInstallClick = async () => {
    console.log('🔘 Botón Instalar clickeado');
    console.log('📱 deferredPrompt:', deferredPrompt);
    
    if (deferredPrompt) {
      console.log('✅ deferredPrompt existe, mostrando prompt nativo...');
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log('📊 Resultado de instalación:', outcome);
        
        if (outcome === 'accepted') {
          console.log('✅ Usuario aceptó la instalación');
          localStorage.setItem('ayudalegal_pwa_installed', 'true');
          setIsVisible(false);
        } else {
          console.log('❌ Usuario rechazó la instalación');
        }
      } catch (error) {
        console.error('❌ Error al mostrar prompt de instalación:', error);
      }
    } else {
      console.warn('⚠️  deferredPrompt es null - el evento beforeinstallprompt no se disparó');
      console.log('💡 Esto puede ocurrir si:');
      console.log('  1. No estás en Android/Chrome');
      console.log('  2. La app ya está instalada');
      console.log('  3. Los criterios PWA no se cumplen completamente');
      console.log('  4. El usuario ya rechazó la instalación previamente');
      
      // Mostrar alerta al usuario
      alert('Para instalar la app:\n\n' +
            '1. Abre Chrome en Android\n' +
            '2. Presiona el menú (⋮) superior derecho\n' +
            '3. Selecciona "Instalar aplicación" o "Añadir a pantalla de inicio"');
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50" style={{ maxWidth: 'calc(100vw - 32px)' }}>
      <div className="w-full max-w-md mx-auto bg-white shadow-2xl rounded-2xl border border-blue-100 p-4 flex items-center space-x-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-white">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="12" y1="2" x2="12" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <line x1="12" y1="12" x2="12" y2="18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <line x1="4" y1="12" x2="12" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <line x1="12" y1="12" x2="20" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="12" r="2" fill="white"/>
          </svg>
        </div>
        <div className="flex-1 text-sm">
          <div className="font-bold text-gray-900 mb-1">Instala AYUDA LEGAL MX</div>
          <div className="text-gray-600 leading-snug">
            Tu app ahora en tu teléfono.
            {isIOS && (
              <>
                <br />
                <span className="text-xs text-gray-500">
                  En iPhone: toca el botón de compartir y elige &quot;Añadir a pantalla de inicio&quot;.
                </span>
              </>
            )}
          </div>
        </div>
        {!isIOS && (
          <button
            onClick={handleInstallClick}
            className="bg-blue-600 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Instalar
          </button>
        )}
        <button
          onClick={handleClose}
          className="text-gray-400 hover:text-gray-600 text-lg px-1"
          aria-label="Cerrar banner de instalación"
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default PwaInstallBanner;
